import { API, graphqlOperation } from "aws-amplify";
import { getZone } from "../graphql/queries";
import i18n from "../i18n";
import _ from "lodash";

export const getSensorString = (value) => {
	if (value === 0 || !value) return i18n.t("common.clear");
	if (value === 1 || value) return i18n.t("common.leak");
};

export const getAlarmString = (value) => {
	if (value === 0 || !value) return i18n.t("common.clear");
	if (value === 1 || value) return i18n.t("common.alarm");
};

export const getSensorResistanceString = (value) => {
	if (value === undefined || value === null) return "--";
	if (value > 2000000) return i18n.t("common.disconnected");
	if (value >= 1000000) return `${(value / 1000000).toFixed(1)}MΩ`;
	if (value >= 1000) return `${Math.round(value / 1000)}kΩ`;
	return `${value}Ω`;
};

//WiFi signal bands, tuned to this fleet rather than to generic chart colours. Pucks start
//dropping out below -70 dBm, which is also where an ESP32 runs out of margin for the higher
//802.11g/n rates, so -70 is the edge of "poor" and not the middle of a comfortable band.
//The previous edges (-60/-70/-80) labelled a failing -75 dBm link "fair".
//Bands are ordered strongest first and minRssi is inclusive, so a reading belongs to the
//first band whose minRssi it meets. Adjust these values if the fleet behaves differently -
//RSSI alone does not predict a drop-out, since the noise floor and SNR matter too.
export const RSSI_BANDS = [
	{ key: "excellent", minRssi: -55 },
	{ key: "good", minRssi: -65 },
	{ key: "weak", minRssi: -70 },
	{ key: "poor", minRssi: -Infinity },
];

//Single classifier for every RSSI surface - the detail page, the dashboard chart and the
//devices table filter. They used to hold three copies of the edges, and the table applied
//them as exclusive lower bounds, so a device reading exactly -60 was "excellent" on the
//chart and "good" in the list the chart linked to.
export const getRssiBand = (rssi) => {
	//Number(null) and Number("") are both 0, which would read as a strong signal rather than
	//as a device that has not reported one, so those are rejected before the conversion.
	if (rssi === null || rssi === undefined || rssi === "") return null;
	const value = Number(rssi);
	if (!Number.isFinite(value)) return null;
	return RSSI_BANDS.find((band) => value >= band.minRssi) ?? null;
};

export const getWifiRssiString = (rssi) => {
	const band = getRssiBand(rssi);
	if (!band) return "--";
	return `${i18n.t(`device.state.wifiRssiQuality.${band.key}`)} (${rssi} dBm)`;
};

export const supportsValveControl = (deviceInfo) => {
	return (
	process.env.REACT_APP_ENABLE_DEVICE_RELAY === "true" &&
	deviceInfo?.outputDevice?.includes("FLOWSTOP")
	);
};

//A FlowStop that has not reported a relay status yet has nothing to toggle from, so callers
//hide the control rather than posting a desired state derived from an unknown value.
export const canControlValve = (deviceInfo, reportedState) =>
	supportsValveControl(deviceInfo) && reportedState?.relay?.status !== undefined;

export const getRelayString = (value) => {
	if (value === undefined || value === null) return "---";
	if (!value) return i18n.t("device.relay.open");
	if (value) return i18n.t("device.relay.closed");
};

export const getValveString = (value) => {
	if (!value) return i18n.t("device.relay.open");
	if (value) return i18n.t("device.relay.closed");
};

//Primary valve/relay state with its config enabled flag - a closed-but-disabled valve is
//configuration, not a fault, so surfaces should label it Disabled rather than alarming
export const getValveSummary = (reportedState) => {
	const candidates = [
		{
			status: reportedState?.valves?.valve1?.status,
			enabled: reportedState?.config?.valves?.valve1?.enabled,
		},
		{
			status: reportedState?.valves?.valve2?.status,
			enabled: reportedState?.config?.valves?.valve2?.enabled,
		},
		//CC relay - no per-output enable flag in config
		{ status: reportedState?.relay?.status, enabled: undefined },
	];

	const primary = candidates.find((candidate) => candidate.status !== undefined && candidate.status !== null);
	if (!primary) return { status: undefined, disabled: false };

	return { status: primary.status, disabled: primary.enabled === false };
};

export const getDcStatusString = (value) => {
	if (!value) return i18n.t("device.dcStatus.false");
	if (value) return i18n.t("device.dcStatus.true");
};

export const getBatteryChargingString = (value) => {
	if (!value) return i18n.t("device.batteryCharging.false");
	if (value) return i18n.t("device.batteryCharging.true");
};

export const getYesNoString = (value) => {
	if (value === undefined) return "---";
	if (value === 0 || !value) return i18n.t("common.option.no");
	if (value === 1 || value) return i18n.t("common.option.yes");
};

export const useGetFullLocation = () => {
	let locationBreadcrumb = [];
	let tenants = [];
	const getFullLocation = async (zone) => {
		locationBreadcrumb = [];
		tenants = [];
		if (zone) await getLocationBreadcrumb(zone);

		const breadcrumbLabel = locationBreadcrumb
			.map((crumb) => crumb.label)
			.reverse()
			.join("|");
		const breadcrumbKey = locationBreadcrumb
			.map((crumb) => crumb.id)
			.reverse()
			.join("|");

		return { locationBreadcrumb, breadcrumbLabel, breadcrumbKey, tenants: _.flattenDeep(tenants) };
	};

	const getLocationBreadcrumb = async (zone) => {
		if (!zone.parent) {
			if (zone.tenant?.length)
				tenants.push({ tenantIds: zone.tenant, zone: { label: zone.label, type: zone.type } });
			locationBreadcrumb.push({ id: zone.id || zone.key, label: zone.label, type: zone.type });
			return locationBreadcrumb;
		} else {
			if (zone.tenant?.length)
				tenants.push({ tenantIds: zone.tenant, zone: { label: zone.label, type: zone.type } });
			locationBreadcrumb.push({ id: zone.id || zone.key, label: zone.label, type: zone.type });
			const {
				data: { getZone: parentZone },
			} = await API.graphql(graphqlOperation(getZone, { id: zone.parent }));
			await getLocationBreadcrumb(parentZone);
		}

		// return locationBreadcrumb;
	};

	return getFullLocation;
};
